// Client-side port of the backend MentorGuideNode prompt. Keeps the SLM
// output identical in shape to the Gemini path so the Chatbot renders both
// the same way.
import { MOVE_TYPES, MODE_MOVES } from "./moveRouter";
import { EMPTY_CANTIERE, EMPTY_FONDAMENTA } from "./summarize";
import { render, renderStr, vocab } from "./prompts";

// Prompt texts live in prompts/<lang>/*.md (+ vocab.json). The exported
// constants below keep their historical names and Italian values; the
// builders take `lang` (default "it").

// Wrap-up eligibility gate: `memory` (the persisted keyword-memory array,
// accumulated across the whole session — see SESSION_MEMORY) is used
// instead of `chat`, since `chat` is only a sliding window of the last
// few messages (see Chatbot.jsx's .slice(-6)/.slice(-3)) and can never
// reflect "a good deal of interaction has happened" on its own. Below
// this threshold the model is told to keep wrap_up forced false, so a
// short, just-started conversation can never wrap up prematurely.
export const WRAP_UP_MIN_MEMORY = 8;
export function isWrapUpEligible(memory, { forceModeQuestion = false, forceModeRedirect = false, modeJustResolved = false, forceCheckpoint = false } = {}) {
  if (forceModeQuestion || forceModeRedirect || modeJustResolved || forceCheckpoint) return false;
  return (memory || []).length >= WRAP_UP_MIN_MEMORY;
}

export const DT_PHASES = [
  "esplorazione_ideazione",
  "prototipazione_iterazione",
  "integrazione_implementazione",
  "evoluzione_scalabilita",
  "interazione_etica",
];

export const TEACHER_STAGES = ["osserva", "intervento", "applica", "feedback"];

export const EVIDENCE_SLOT_KEYS = ["problema_chiarito", "alternative_esplorate", "criterio_scelta", "esperimento_definito"];
// Mirrors backend/pocketflow_agents.py's ALL_EVIDENCE_SLOT_KEYS — "settore"
// is captured (same permanence rule) but excluded from EVIDENCE_SLOT_KEYS,
// so canAdvancePhase never requires it.
export const ALL_EVIDENCE_SLOT_KEYS = [...EVIDENCE_SLOT_KEYS, "settore"];
export const EMPTY_EVIDENCE_SLOTS = Object.fromEntries(ALL_EVIDENCE_SLOT_KEYS.map((k) => [k, ""]));
export const EVIDENCE_SLOT_LABELS = vocab("it").evidence_slot_labels;

// One step forward in the osserva->intervento->applica->feedback cycle,
// wrapping around. An unrecognized stage is treated as if it were "osserva"
// (so the result is "intervento"). Mirrors
// backend/pocketflow_agents.py's next_teacher_stage.
export function nextTeacherStage(stage) {
  const idx = TEACHER_STAGES.includes(stage) ? TEACHER_STAGES.indexOf(stage) : 0;
  return TEACHER_STAGES[(idx + 1) % TEACHER_STAGES.length];
}

// Deterministic, in code — mirrors buildMentorMessages' own stage-resolution
// branch. Exported so a caller (slmMentor.js/engineBridge.js) can know
// teacherStageForPrompt BEFORE calling buildMentorMessages, since it needs
// that value to decide whether to fetch RAG knowledge at all (intervento
// only) ahead of time — the fetch happens outside buildMentorMessages.
export function resolveTeacherStage({
  prevTeacherStage, advanceAllowed = false, forceModeQuestion = false,
  forceModeRedirect = false, forceCheckpoint = false, modeJustResolved = false, intent = "mentor",
}) {
  const teacherStage = TEACHER_STAGES.includes(prevTeacherStage) ? prevTeacherStage : "osserva";
  if (advanceAllowed) {
    return { teacherStageForPrompt: "osserva", teacherStageOut: nextTeacherStage("osserva"), teacherStageIn: teacherStage };
  }
  if (forceModeQuestion || forceModeRedirect || forceCheckpoint || modeJustResolved || intent !== "mentor") {
    return { teacherStageForPrompt: teacherStage, teacherStageOut: teacherStage, teacherStageIn: teacherStage };
  }
  return { teacherStageForPrompt: teacherStage, teacherStageOut: nextTeacherStage(teacherStage), teacherStageIn: teacherStage };
}

// The current phase's still-relevant decisions (proposta/confermata), most
// recent last, capped at `limit`. Mirrors
// backend/pocketflow_agents.py's open_decisions_for_phase.
export function openDecisionsForPhase(prevBrief, phase, limit = 6) {
  return (prevBrief?.decisions || [])
    .filter((d) => d.phase === phase && (d.status === "proposta" || d.status === "confermata"))
    .slice(-limit);
}

// ChromaDB L2 distance above which a retrieved chunk is treated as not
// actually relevant. Mirrors backend/pocketflow_agents.py's
// RAG_RELEVANCE_THRESHOLD (same calibration).
export const RAG_RELEVANCE_THRESHOLD = 1.1;

// Replaces (not appends to) the intervento instruction when the retrieved
// material misses RAG_RELEVANCE_THRESHOLD (or nothing was found at all).
// Mirrors backend/pocketflow_agents.py's INTERVENTO_NEEDS_CLARIFICATION_INSTRUCTION.
export const INTERVENTO_NEEDS_CLARIFICATION_INSTRUCTION = render("intervento_needs_clarification", "it");

// True once all 4 concrete evidence-slot fields for `phase` are filled —
// replaces the old 2-keyword-count gate; a phase "closing" now requires
// actual reasoning artifacts (problem, alternatives, criterion, experiment),
// not two incidental keywords. Pure check — mirrors
// backend/pocketflow_agents.py's can_advance_phase.
export function canAdvancePhase(evidenceSlots, phase) {
  const slots = (evidenceSlots || {})[phase] || {};
  return EVIDENCE_SLOT_KEYS.every((k) => (slots[k] || "").trim());
}

// Fills empty evidence-slot fields for `phase` from a delta; a slot already
// filled is never cleared or overwritten (same permanence rule as
// fondamenta.vincoli/preferenze_metodo). Ensures every DT_PHASES entry
// exists in the returned object. Pure, mirrors
// backend/pocketflow_agents.py's merge_evidence_slots.
export function mergeEvidenceSlots(prevSlots, deltaSlots, phase) {
  const prev = prevSlots || {};
  const result = {};
  for (const p of DT_PHASES) result[p] = { ...(prev[p] || EMPTY_EVIDENCE_SLOTS) };
  const delta = deltaSlots || {};
  const current = { ...result[phase] };
  for (const k of ALL_EVIDENCE_SLOT_KEYS) {
    if (current[k]) continue;
    const v = delta[k];
    // Capped like every other stored brief field (evidenze_chiave[:8],
    // carte_collegate[:10], DECISIONS_CAP, CANTIERE_CAPS): a slot value is
    // permanent and re-serialized into every subsequent prompt, so an
    // unbounded model ramble here would grow the prompt forever.
    if (typeof v === "string" && v.trim()) current[k] = v.trim().slice(0, 200);
  }
  result[phase] = current;
  return result;
}

export const EMPTY_BRIEF = {
  obiettivo: "", evidenze_chiave: [], carte_collegate: [], direzione: "", sintesi_processo: "",
  cantiere: EMPTY_CANTIERE, fondamenta: EMPTY_FONDAMENTA, decisions: [],
  evidence_slots: Object.fromEntries(DT_PHASES.map((p) => [p, { ...EMPTY_EVIDENCE_SLOTS }])),
};

// Move-specific instruction that replaces the default "ask one question" step.
// No future_projection entry: scenari_futuri has no dedicated move (see
// moveRouter.js's MODE_MOVES comment) — finding future scenarios is an
// implicit background goal, not a chat move that gets announced.
const MOVE_PROMPTS = {
  question: "mentor_base_question_js",
  deepen: "move_deepen",
  explain_steps: "move_explain_steps",
  lateral: "move_lateral",
  organic: "move_organic",
  map_connection: "move_map_connection",
};
export const moveInstructions = (lang = "it") =>
  Object.fromEntries(Object.entries(MOVE_PROMPTS).map(([move, name]) => [move, render(name, lang)]));
export const MOVE_INSTRUCTIONS = moveInstructions("it");

// Instruction text for the current step of the osserva->intervento->applica
// ->feedback teacher cycle (Task 3's TEACHER_STAGES). Replaces
// MOVE_INSTRUCTIONS[move] as the source of the "MOSSA DI QUESTO TURNO" text
// for a normal turn. Mirrors backend/pocketflow_agents.py's MentorGuideNode.exec.
const teacherStageInstructions = (lang) =>
  Object.fromEntries(TEACHER_STAGES.map((s) => [s, render(`teacher_stage_${s}`, lang)]));

// Per-mode style steer appended to the teacher-stage instruction. Mirrors
// backend/pocketflow_agents.py's MODE_STYLE_INSTRUCTIONS verbatim (one
// entry, for mappatura) — NOT MOVE_INSTRUCTIONS.map_connection, which was
// written as a *replacement* for the default question instruction (its
// closing "NON fare la domanda standard" has no referent once appended
// after a teacher-stage instruction instead, and directly contradicts
// whatever move that stage just asked for).
// No scenari_futuri entry (mirrors moveRouter.js's MODE_MOVES): finding
// future scenarios is an implicit background goal, never a style to
// announce in chat — forking is proposed by a separate pass.
const modeStyleInstructions = (lang) => ({ mappatura: render("mode_style_mappatura", lang) });

export const MODE_QUESTION = render("mode_question", "it");
export const MODE_REDIRECT = render("mode_redirect", "it");
export const GUARDRAIL_INSTRUCTION = render("guardrail", "it");
export const CHECKPOINT_INSTRUCTION = render("checkpoint", "it");
export const CHECKPOINT_SUGGESTION_OVERRIDE = render("checkpoint_suggestion_override", "it");

function briefIsEmpty(b) {
  return !b || (!b.obiettivo && !b.direzione && !b.sintesi_processo &&
    !(b.evidenze_chiave || []).length && !(b.carte_collegate || []).length);
}

export function buildMentorMessages({
  chat,
  purpose,
  usedCards = [],
  unusedCards = [],
  knowledge = "",
  memory = [],
  move = "question",
  prevBrief = null,
  intent = "mentor",
  prevPhase = "esplorazione_ideazione",
  mode = null,
  forceModeQuestion = false,
  forceModeRedirect = false,
  modeJustResolved = false,
  forceCheckpoint = false,
  advanceAllowed = false,
  prevTeacherStage = "osserva",
  scenarioTree = "",
  likelyBranchId = null,
  needsClarification = false,
  lang = "it",
}) {
  const v = vocab(lang);
  const phaseDesc = v.phase_descriptions;
  const currentPhase = prevPhase;
  const nextIdx = Math.min(DT_PHASES.indexOf(currentPhase) + 1, DT_PHASES.length - 1);
  const nextPhase = DT_PHASES[nextIdx];

  const allLabels = [...usedCards, ...unusedCards].map((c) => c.label);
  const priorStr = memory.length
    ? memory.map((e) => `[${e.phase}] ${e.text}`).join("; ")
    : v.none_yet;

  // Deterministic, in code — same precedent as validatePhase forcing the
  // phase advance and decideScenarioAction owning the "whether" for
  // scenario forks: the model is never asked which stage it's in, it's
  // just told and follows.
  const { teacherStageForPrompt, teacherStageOut, teacherStageIn: teacherStage } = resolveTeacherStage({
    prevTeacherStage, advanceAllowed, forceModeQuestion, forceModeRedirect, forceCheckpoint, modeJustResolved, intent,
  });

  const prevEvidenceSlots = prevBrief?.evidence_slots;
  const missingSlots = EVIDENCE_SLOT_KEYS.filter(
    (k) => !((prevEvidenceSlots || {})[prevPhase]?.[k] || "").trim()
  );
  const focusNote = missingSlots.length
    ? render("mentor_focus_note", lang, { slots: missingSlots.map((k) => v.evidence_slot_labels[k]).join(", ") })
    : "";

  let decisionsForTeacher = "";
  if (teacherStageForPrompt === "intervento") {
    const relevantDecisions = openDecisionsForPhase(prevBrief, prevPhase);
    if (relevantDecisions.length) {
      const lines = relevantDecisions
        .map((d) => renderStr("mentor_decision_line", lang, {
          status: d.status, text: d.text, motivazione: d.motivazione || v.not_clear_yet,
        }))
        .join("\n");
      decisionsForTeacher = render("mentor_decisions_block", lang, { lines });
    }
  }

  const system = render("mentor_main_js_system", lang);

  const modeStyle = modeStyleInstructions(lang)[mode];
  const moveInstruction = forceModeQuestion ? render("mode_question", lang)
    : forceModeRedirect ? render("mode_redirect", lang)
    : forceCheckpoint ? render("checkpoint", lang)
    : ((teacherStageForPrompt === "intervento" && needsClarification)
        ? render("intervento_needs_clarification", lang)
        : (teacherStageInstructions(lang)[teacherStageForPrompt] || render(MOVE_PROMPTS.question, lang)))
      + (focusNote ? `\n   ${focusNote}` : "")
      + (modeStyle ? `\n   ${modeStyle}` : "");
  // The user hasn't said how they want to work yet (or just did, on THIS
  // turn — modeJustResolved) — objective and approach are the two
  // preparatory questions and must resolve BEFORE any card/note lands on
  // the board. Suggesting cards here forces a kanban layout that a mode
  // chosen on this or the very next turn (a different board entirely)
  // would immediately have to redo.
  const suggestionInstruction = (forceModeQuestion || forceModeRedirect || modeJustResolved)
    ? render("mentor_no_suggestions", lang)
    : forceCheckpoint
    ? render("checkpoint_suggestion_override", lang)
    : mode === "scenari_futuri"
    ? render("mentor_suggestions_scenari", lang)
    : renderStr("mentor_suggestions_default", lang, { current_phase: currentPhase, next_phase: nextPhase });
  // Whether/where to fork and whether a card helps are both decided by a
  // separate, single-purpose pass after this call (see
  // scenarioActionPrompt.js) — this prompt no longer carries the tree detail
  // or a splits instruction at all, which is the whole point: a weak/local
  // model kept dropping that decision when it was one of 6+ concerns
  // packed into this same response. All that's left here for scenari_futuri
  // is the one-line "don't bother" note folded into suggestionInstruction
  // above.
  const wrapUpEligible = isWrapUpEligible(memory, { forceModeQuestion, forceModeRedirect, modeJustResolved, forceCheckpoint });
  const wrapUpInstruction = wrapUpEligible ? render("mentor_wrap_up_js", lang) : "";
  const briefBlock = briefIsEmpty(prevBrief) ? "" :
    render("mentor_brief_block", lang, { brief_json: JSON.stringify(prevBrief) });

  // advanceAllowed is only ever true the turn right after the user confirms
  // a pending checkpoint — the phase advance itself is now forced by
  // validatePhase in slmMentor.js regardless of what "phase" the model
  // returns here, so this instruction only shapes the MESSAGE (a natural
  // bridge into the new phase), not a decision the model still has to make.
  const advanceInstruction = advanceAllowed
    ? renderStr("mentor_advance_js", lang, { next_phase: nextPhase, next_phase_desc: phaseDesc[nextPhase] })
    : "";

  const isLightweight = intent === "chat" || intent === "knowledge";
  const istruzioni = isLightweight
    ? render("mentor_instructions_light_js", lang)
    : renderStr("mentor_instructions_full_js", lang, {
      move_instruction: moveInstruction,
      suggestion_instruction: suggestionInstruction,
      current_phase: currentPhase,
      advance: advanceInstruction,
      wrap_up: wrapUpInstruction,
    });

  const user = renderStr("mentor_main_js", lang, {
    purpose: purpose || v.not_defined_yet,
    current_phase: currentPhase,
    current_phase_desc: phaseDesc[currentPhase],
    next_phase: nextPhase,
    next_phase_desc: phaseDesc[nextPhase],
    chat,
    cards_on_table: JSON.stringify(usedCards.map(c => c.descrizioneBreve ? `${c.label} (${c.descrizioneBreve})` : c.label)),
    cards_available: JSON.stringify(allLabels.slice(0, 25)),
    prior: priorStr,
    brief_block: briefBlock,
    decisions_block: decisionsForTeacher,
    knowledge: knowledge || v.no_context_retrieved,
    guardrail: render("guardrail", lang),
    istruzioni,
    move,
  });

  return {
    messages: [
      { role: "system", content: system },
      { role: "user", content: user },
    ],
    currentPhase,
    allLabels,
    wrapUpEligible,
    teacherStageOut,
    // The validated PRE-advance stage. Handed forward so a turn whose
    // response never parsed can hold the cycle where it was, exactly like
    // backend/pocketflow_agents.py's total-failure fallback returns
    // teacher_stage (not teacher_stage_out).
    teacherStageIn: teacherStage,
  };
}

// Strict output schema (JSON string) shared by every local path (WebLLM,
// Engine). Grammar-constrains decoding: real keys, enum phases/zones, card
// labels limited to the actual deck. Label enum only when cards exist —
// an empty enum is invalid JSON schema.
export function buildMentorSchema(allLabels, mode) {
  const labelSchema = allLabels.length
    ? { type: "string", enum: allLabels }
    : { type: "string" };
  const modeMove = MODE_MOVES[mode];
  const moveEnum = modeMove ? [...MOVE_TYPES, modeMove] : MOVE_TYPES;
  return JSON.stringify({
    type: "object",
    properties: {
      message: { type: "string" },
      phase: { type: "string", enum: DT_PHASES },
      wrap_up: { type: "boolean" },
      suggested_cards: {
        type: "array",
        items: {
          type: "object",
          properties: { label: labelSchema, zone: { type: "string", enum: DT_PHASES } },
          required: ["label", "zone"],
        },
      },
      suggested_notes: {
        type: "array",
        items: {
          type: "object",
          properties: { phase: { type: "string", enum: DT_PHASES }, text: { type: "string" } },
          required: ["phase", "text"],
        },
      },
      suggested_splits: {
        type: "array",
        items: {
          type: "object",
          properties: {
            afterNodeId: { type: "string" },
            branches: { type: "array", items: { type: "string" } },
          },
          required: ["afterNodeId", "branches"],
        },
      },
      evidences: {
        type: "array",
        items: {
          type: "object",
          properties: { phase: { type: "string", enum: DT_PHASES }, text: { type: "string" } },
          required: ["phase", "text"],
        },
      },
      move_type: { type: "string", enum: moveEnum },
      evidence_slots: {
        type: "object",
        properties: Object.fromEntries(ALL_EVIDENCE_SLOT_KEYS.map((k) => [k, { type: "string" }])),
        required: [...ALL_EVIDENCE_SLOT_KEYS],
      },
      brief: {
        type: "object",
        properties: {
          evidenze_chiave: {
            type: "array",
            items: { type: "object",
              properties: { phase: { type: "string", enum: DT_PHASES }, text: { type: "string" } },
              required: ["phase", "text"] },
          },
          carte_collegate: {
            type: "array",
            items: { type: "object",
              properties: { label: { type: "string" }, by: { type: "string", enum: ["utente", "mentor"] }, perche: { type: "string" } },
              required: ["label", "by"] },
          },
          direzione: { type: "string" },
        },
      },
    },
    required: ["message", "phase", "suggested_cards", "evidences", "evidence_slots"],
  });
}
